import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useCards } from '../../hooks/useApi'
import { ROUTES } from '@shared/config/routes'
import type { BasketItem } from '@entities/calculator'
import {
  isPaywallStatus,
  useBasketQuery,
  useCalculatorCardsQuery,
  useCalculatorDictionaryQuery,
  useCalculatorMetadataQuery,
  useRefetchDictionaryOnUnknown,
  useSavedRecipesQuery,
} from '@entities/calculator'
import { usePageAnalytics } from '@features/page-analytics'
import { CalculatorAnalyticsContext } from './components/analytics'
import CostTabs, { type CostTab } from './components/CostTabs'
import {
  OverlayBackContext,
  useOverlayBackStack,
} from './components/overlayBack'
import ProductsTab from './components/ProductsTab'
import RatesTab from './components/RatesTab'
import RecipesTab, { type RecipeCardOption } from './components/RecipesTab'
import './CostCalculatorPage.css'

/** Один и тот же пустой массив: новый литерал сбрасывал бы мемо у вкладок. */
const EMPTY_ITEMS: readonly BasketItem[] = []
const EMPTY_IDS: readonly number[] = []

/**
 * Себестоимость: свои цены закупки на вкладке «Мои продукты» и расчёт по
 * рецептам на «Моих рецептах».
 *
 * Раздел целиком платный — любая ручка без активной подписки отдаёт 403.
 * Проверять подписку заранее не надо, достаточно показать 403 по-человечески.
 */
const CostCalculatorPage = () => {
  const [tab, setTab] = useState<CostTab>('products')
  // Открытый оверлей забирает кнопку «Назад» себе: иначе она уводит со
  // страницы, а список или форма остаются висеть.
  // Заход на страницу хук отправляет сам, вместе с временем на ней.
  const { trackAction } = usePageAnalytics('cost_calculator')
  const { api: overlayBack, onBack } = useOverlayBackStack()
  useTelegramBackButton(true, onBack)

  const metadataQuery = useCalculatorMetadataQuery()
  const dictionaryQuery = useCalculatorDictionaryQuery()
  const calculatorCardsQuery = useCalculatorCardsQuery()
  const basketQuery = useBasketQuery()
  const savedQuery = useSavedRecipesQuery()
  const { data: cardsData, isLoading: isCatalogLoading } = useCards()

  const errors = [
    metadataQuery.error,
    dictionaryQuery.error,
    calculatorCardsQuery.error,
    basketQuery.error,
    savedQuery.error,
  ]

  // Достаточно одного 403: раздел платный целиком, остальные ручки ответят так же.
  const isPaywalled = errors.some(error =>
    isPaywallStatus(error?.response?.status)
  )
  // Любая другая ошибка: у всех запросов retry: false, и без этой ветки человек
  // читал бы «пока пусто» — то есть экран врал бы, что ничего не сохранено.
  const hasFailed = !isPaywalled && errors.some(Boolean)

  const basketItems = basketQuery.data?.items ?? EMPTY_ITEMS
  const ingredients = dictionaryQuery.data?.ingredients ?? []

  // Контракт: встретился идентификатор не из справочника — справочник надо
  // перезапросить, там могли появиться новые позиции.
  const unknownIds = useMemo(() => {
    const known = new Set(ingredients.map(item => item.ingredient_id))
    return basketItems
      .map(item => item.ingredient_id)
      .filter(id => known.size > 0 && !known.has(id))
  }, [basketItems, ingredients])
  useRefetchDictionaryOnUnknown(unknownIds)

  // Названия и обложки — из каталога карточек, в калькуляторных ручках их нет.
  const recipeCards = useMemo<RecipeCardOption[]>(() => {
    const ids = calculatorCardsQuery.data?.card_ids ?? []
    const byId = new Map(
      (cardsData?.cards ?? []).map(card => [card.card_id, card])
    )
    return ids
      .map(cardId => {
        const card = byId.get(cardId)
        return {
          cardId,
          title: card?.title ?? `Рецепт №${cardId}`,
          icon: card?.icon_url ?? '',
        }
      })
      .sort((a, b) => a.title.localeCompare(b.title, 'ru'))
  }, [calculatorCardsQuery.data, cardsData])

  if (isPaywalled) {
    return (
      <div className="cost-page">
        <div className="cost-body">
          <h1 className="cost-title">Себестоимость</h1>
          <p className="cost-note">
            Расчёт себестоимости входит в подписку. Оформите её — сохраним ваши
            цены на продукты и посчитаем стоимость любого рецепта.
          </p>
          <Link className="cost-button wide" to={ROUTES.SUBSCRIBE}>
            Оформить подписку
          </Link>
        </div>
      </div>
    )
  }

  if (hasFailed) {
    return (
      <div className="cost-page">
        <div className="cost-body">
          <h1 className="cost-title">Себестоимость</h1>
          <p className="cost-note warning">
            <span>
              Не удалось загрузить данные. Ваши цены на месте — попробуйте ещё
              раз.
            </span>
          </p>
          <button
            type="button"
            className="cost-button wide"
            onClick={() => {
              void metadataQuery.refetch()
              void dictionaryQuery.refetch()
              void calculatorCardsQuery.refetch()
              void basketQuery.refetch()
              void savedQuery.refetch()
            }}
          >
            Повторить
          </button>
        </div>
      </div>
    )
  }

  return (
    <CalculatorAnalyticsContext.Provider value={trackAction}>
      <OverlayBackContext.Provider value={overlayBack}>
        <div className="cost-page">
          <div className="cost-body">
            <h1 className="cost-title">Себестоимость</h1>

            <CostTabs
              value={tab}
              onChange={setTab}
              productsCount={basketItems.length}
            />

            {tab === 'rates' ? (
              <RatesTab
                roles={dictionaryQuery.data?.labour_roles ?? []}
                isLoading={dictionaryQuery.isLoading}
              />
            ) : tab === 'products' ? (
              <ProductsTab
                items={basketItems}
                ingredients={ingredients}
                categories={dictionaryQuery.data?.categories ?? []}
                isLoading={basketQuery.isLoading || dictionaryQuery.isLoading}
              />
            ) : (
              <RecipesTab
                cards={recipeCards}
                savedIds={savedQuery.data?.card_ids ?? EMPTY_IDS}
                dictionary={dictionaryQuery.data}
                metadata={metadataQuery.data?.metadata}
                basketItems={basketItems}
                // Каталог ждём тоже: без него в списке стояли бы заглушки
                // «Рецепт №12», и порядок поменялся бы под пальцем.
                isLoading={
                  calculatorCardsQuery.isLoading ||
                  isCatalogLoading ||
                  savedQuery.isLoading
                }
                onGoToRates={() => setTab('rates')}
              />
            )}
          </div>
        </div>
      </OverlayBackContext.Provider>
    </CalculatorAnalyticsContext.Provider>
  )
}

export default CostCalculatorPage
